/** Shared constants used by the API, dashboard and player. */

export const ORGANIZATION_ROLES = ['owner', 'admin', 'content_manager', 'viewer'] as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

export const PLATFORM_ROLES = ['user', 'super_admin'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/** The languages of the dashboard and of Signage's emails. */
export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];

export const SCREEN_ORIENTATIONS = ['landscape', 'portrait'] as const;
export type ScreenOrientation = (typeof SCREEN_ORIENTATIONS)[number];

export const SCALING_MODES = ['fit', 'fill', 'stretch', 'original'] as const;
export type ScalingMode = (typeof SCALING_MODES)[number];

export const TRANSITIONS = ['none', 'fade', 'slide'] as const;
export type Transition = (typeof TRANSITIONS)[number];

export const MEDIA_KINDS = ['image', 'video'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const MEDIA_STATUSES = ['uploading', 'processing', 'ready', 'failed'] as const;
export type MediaStatus = (typeof MEDIA_STATUSES)[number];

export const SCREEN_STATUSES = ['online', 'syncing', 'warning', 'offline'] as const;
export type ScreenStatus = (typeof SCREEN_STATUSES)[number];

export const CONTENT_TYPES = ['playlist', 'slideshow', 'media', 'menu'] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

export const SCHEDULE_TARGET_TYPES = ['screen', 'location', 'tag', 'all'] as const;
export type ScheduleTargetType = (typeof SCHEDULE_TARGET_TYPES)[number];

export const ORGANIZATION_STATUSES = ['active', 'suspended', 'deleted'] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'canceled',
  'incomplete',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const NOTIFICATION_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

export const SYNC_STATES = ['idle', 'syncing', 'synced', 'failed'] as const;
export type SyncState = (typeof SYNC_STATES)[number];

/** Allowed upload MIME types. Extensions alone are never trusted. */
export const ALLOWED_IMAGE_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;
export const ALLOWED_VIDEO_MIME = ['video/mp4', 'video/webm'] as const;
export const ALLOWED_MIME = [...ALLOWED_IMAGE_MIME, ...ALLOWED_VIDEO_MIME] as const;

export const MIME_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
};

export function mediaKindForMime(mime: string): MediaKind | null {
  if ((ALLOWED_IMAGE_MIME as readonly string[]).includes(mime)) return 'image';
  if ((ALLOWED_VIDEO_MIME as readonly string[]).includes(mime)) return 'video';
  return null;
}

/** Default duration for images / slides, in milliseconds. */
export const DEFAULT_IMAGE_DURATION_MS = 10_000;

/** Common resolutions offered in the UI; custom values are allowed too. */
export const COMMON_RESOLUTIONS = [
  { label: '1920 x 1080 (Full HD landscape)', width: 1920, height: 1080 },
  { label: '1080 x 1920 (Full HD portrait)', width: 1080, height: 1920 },
  { label: '3840 x 2160 (4K landscape)', width: 3840, height: 2160 },
  { label: '2160 x 3840 (4K portrait)', width: 2160, height: 3840 },
  { label: '1280 x 720 (HD landscape)', width: 1280, height: 720 },
  { label: '720 x 1280 (HD portrait)', width: 720, height: 1280 },
] as const;

/** Player defaults; the server may override them per organization/screen. */
export const PLAYER_DEFAULTS = {
  heartbeatIntervalSec: 30,
  contentCheckIntervalSec: 120,
  proofOfPlayFlushIntervalSec: 300,
  proofOfPlayBatchSize: 200,
  maxCacheBytes: 20 * 1024 * 1024 * 1024,
  downloadConcurrency: 2,
  screenshotEnabled: true,
} as const;

/** A screen is considered offline after this many missed heartbeats (server side). */
export const OFFLINE_AFTER_SEC = 150;
export const WARNING_AFTER_SEC = 90;
